import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchRoles } from '@/store/thunks/roleThunks';
import { grantRolesToUser } from '@/store/thunks/userThunks';
import type { User, Role } from '@/types';
import { Modal, Button } from '@/components/ui';

interface UserRolesModalProps {
  user: User | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function roleIdSet(user: User | null): Set<number> {
  const list = (user?.roles ?? []) as Role[];
  return new Set(list.map((r) => r.id).filter(Boolean));
}

export default function UserRolesModal({ user, open, onClose, onSuccess }: UserRolesModalProps) {
  const dispatch = useAppDispatch();
  const roles = useAppSelector((s: RootState) => s.role.roles);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) dispatch(fetchRoles());
  }, [open, dispatch]);

  useEffect(() => {
    if (open && user) setSelectedIds(roleIdSet(user));
  }, [open, user]);

  const toggleRole = (r: Role) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(r.id)) next.delete(r.id);
      else next.add(r.id);
      return next;
    });
  };

  const selectAll = () => {
    const allSelected = roles.length > 0 && roles.every((r) => selectedIds.has(r.id));
    setSelectedIds(allSelected ? new Set() : new Set(roles.map((r) => r.id)));
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const selectedRoles = roles.filter((r) => selectedIds.has(r.id));
      const res = await dispatch(grantRolesToUser({ userId: user.id, roles: selectedRoles }));
      if (grantRolesToUser.fulfilled.match(res)) {
        onSuccess?.();
        onClose();
      }
    } catch {
      // toast in thunk
    } finally {
      setLoading(false);
    }
  };

  const displayName = [user?.nom, user?.prenom].filter(Boolean).join(' ') || user?.login || user?.code || `Utilisateur #${user?.id ?? ''}`;
  const allSelected = roles.length > 0 && roles.every((r) => selectedIds.has(r.id));

  return (
    <Modal open={open} onClose={onClose} title={`Attribuer les rôles — ${displayName}`} size="lg">
      <div className="flex flex-col gap-3">
        {roles.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2">
            <span className="text-sm font-medium text-slate-600">
              {selectedIds.size} rôle{selectedIds.size !== 1 ? 's' : ''} sélectionné{selectedIds.size !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={selectAll}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
            >
              {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>
        )}

        <div className="min-h-0 max-h-[45vh] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/30">
          {roles.length === 0 ? (
            <p className="p-4 text-center text-sm text-slate-500">Aucun rôle disponible.</p>
          ) : (
            <ul className="divide-y divide-slate-200/80 p-1">
              {roles.map((r) => {
                const name = r.nom ?? r.name ?? r.code ?? `Rôle #${r.id}`;
                const checked = selectedIds.has(r.id);
                return (
                  <li key={r.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-100/80">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(r)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500/20"
                      />
                      <span className="flex-1 font-medium">{name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button type="button" onClick={handleSave} loading={loading} disabled={roles.length === 0}>
          Enregistrer
        </Button>
      </div>
    </Modal>
  );
}
